import React, { useState, useEffect, useRef, useCallback } from 'react';
import { parseString } from 'xml2js';
import CourseMenu from './CourseMenu';
import ScormCourseViewer from './ScormCourseViewer';
import APIwrapper from '../lib/APIwrapper.util.js';
import scormApi12 from '../lib/scormApi12.util.js';
import scormApi2004 from '../lib/scormApi2004.util.js';
import scormToXAPIFunctions from '../lib/SCORMToXAPIFunctions.js';
import { useLocation } from 'react-router-dom';
import {
  LRS_ENDPOINT,
  LRS_API_KEY,
  LRS_API_KEY_SECRETE,
  GetScormCoursesBaseUrl,
  SCORM_VIEWER_URL,
  BACKEND_API_URL,
  WEB_URL
} from '../constants/config';
import Overlay from './Overlay';
import { v4 as uuidv4 } from 'uuid';

let baseUrl = "";
let redirectURL = "";
let delayXApiRequest = null;

/**
 * Storage keys for persisting registration IDs
 */
const REGISTRATION_STORAGE_PREFIX = 'scorm_registration_';

/**
 * xAPI verbs that indicate course completion.
 * Covers standard ADL verbs and the Tin Can API registry variant.
 * When checking completion, we also inspect result.completion and result.success
 * to handle authoring tools that set those instead of (or alongside) specific verbs.
 */
const XAPI_COMPLETION_VERBS = [
  'http://adlnet.gov/expapi/verbs/completed',
  'http://adlnet.gov/expapi/verbs/passed',
  'http://adlnet.gov/expapi/verbs/mastered',
  'http://id.tincanapi.com/verb/completed'
];

/** 
 * 2. resetScorm - Clear out any existing SCORM or xAPI environment 
 */
function resetScorm() {
  APIwrapper.destroyWrapper();
  delete window.initializeCommunication;
  delete window.terminateCommunication;
  delete window.storeDataValue;
  delete window.retrieveDataValue;
  delete window.API;
  delete window.API_1484_11;
  delete window.xapi;
  delete window.localStorage.APIScormConfig;
  delete window.localStorage.learnerId;
}

/** 
 * 3. Build xAPI configuration data 
 */
function getXapiConfig(courseId, courseName, isScorm2004, registrationId) {
    const lmsHomePage = SCORM_VIEWER_URL;
    const activityId = `${lmsHomePage}/${courseId}`;
    return {
        lrs: {
            endpoint: LRS_ENDPOINT,
            user: LRS_API_KEY,
            password: LRS_API_KEY_SECRETE
        },
        isScorm2004,
        lmsHomePage,
        courseId: activityId,
        activityId,
        registration: registrationId,
        groupingContextActivity: {
            definition: {
                name: {
                    'en-US': courseName
                }
            },
            id: activityId,
            objectType: 'Activity'
        }
    };
}

/** 
 * 4. Decode suspend_data 
 */
function decodeSuspendData(suspendData) {
    try {
      if (!suspendData) {
        debugLog('No suspend_data found.');
        return {};
      }
  
      // Try direct JSON parse first
      try {
        const parsed = JSON.parse(suspendData);
        console.log('Parsed suspend_data (as plain JSON):', parsed);
        return parsed;
      } catch (jsonError) {
        console.warn('Direct JSON parse failed, trying utf-16le decoding...');
      }
  
      // Try parsing if it's in the custom UTF-16LE encoded format
      const parsed = JSON.parse(suspendData);
      if (parsed && Array.isArray(parsed.d)) {
        const bytes = [];
  
        parsed.d.forEach((num, index) => {
          const lowByte = num & 0xff;
          const highByte = (num >> 8) & 0xff;
          bytes.push(lowByte, highByte);
          if (index < 5) {
            console.log(`Code unit ${index}: ${num} -> bytes: ${lowByte}, ${highByte}`);
          }
        });
  
        const uint8Array = new Uint8Array(bytes);
        const decoder = new TextDecoder('utf-16le');
        const decodedString = decoder.decode(uint8Array);
  
        const decodedData = JSON.parse(decodedString);
        return decodedData;
      }
    } catch (error) {
      debugLog('Error decoding suspend_data:', error);
    }
  
    return {};
}

/** 
 * 5. SCORM Initialization 
 */
function initScorm(courseData, userData, isScorm2004, registrationId, handleCourseCompletion, isViewOnly = false, passingScore = null) {
    resetScorm();
  
    // Create the SCORM API object in the window
    if (isScorm2004) {
      window.API_1484_11 = new scormApi2004.ScormAPI2004();
    } else {
      window.API = new scormApi12.ScormAPI();
    }
  
    const simplifyScorm = APIwrapper.findAPI(window);
    if (!simplifyScorm) {
      debugLog('simplifyScorm not found', APIwrapper);
      return;
    }
  
    // Initialize xAPI bridging if not in view-only mode
    let xApiFunctions = null;
    if (!isViewOnly) {
      xApiFunctions = scormToXAPIFunctions.xapi();
      if (!xApiFunctions) {
        debugLog('scormToXAPIFunctions.xapi() returned undefined');
        return;
      }
      window.xapi = xApiFunctions;
  
      // Build & store xAPI config
      const xApiConfig = getXapiConfig(courseData.courseId, courseData.name, isScorm2004, registrationId);
      window.localStorage.APIScormConfig = JSON.stringify(xApiConfig);
    }
  
    // Assign student info to SCORM data model
    if (isScorm2004) {
      simplifyScorm.cmi.learner_id = userData.email;
      if (isViewOnly) {
        // Set read-only mode for SCORM 2004
        simplifyScorm.cmi.mode = "review";
        simplifyScorm.cmi.completion_status = "completed";
      }
    } else {
      simplifyScorm.cmi.core.student_id = userData.email;
      if (isViewOnly) {
        // Set read-only mode for SCORM 1.2
        simplifyScorm.cmi.core.lesson_mode = "review";
        simplifyScorm.cmi.core.lesson_status = "completed";
      }
    }
    window.localStorage.learnerId = userData.email;

    // Set passing score in SCORM data model (must be set BEFORE Initialize)
    // This enables automatic pass/fail detection based on score
    if (passingScore !== null && !isNaN(parseFloat(passingScore))) {
      let score = parseFloat(passingScore);
      if (isScorm2004) {
        // SCORM 2004: scaled_passing_score range is -1.0 to 1.0
        // URL param comes as 0-100 (percentage), manifest as 0.0-1.0
        if (score > 1) {
          score = score / 100;
        }
        simplifyScorm.cmi.scaled_passing_score = String(score);
        debugLog(`Set cmi.scaled_passing_score = ${score}`);
      } else {
        // SCORM 1.2: mastery_score is typically 0-100
        simplifyScorm.cmi.student_data.mastery_score = String(score);
        debugLog(`Set cmi.student_data.mastery_score = ${score}`);
      }
    }

    // Set up basic communication functions (needed even in view-only mode)
    window.terminateCommunication = APIwrapper.terminateCommunication;
    window.storeDataValue = APIwrapper.storeDataValue;
    window.retrieveDataValue = APIwrapper.retrieveDataValue;
  
    // Skip all the event handling and state restoration in view-only mode
    if (isViewOnly) {
      debugLog('SCORM initialized in view-only mode - skipping state restoration and event handlers');
      
      // Override store/commit/finish to be no-ops in view-only mode
      if (isScorm2004) {
        simplifyScorm.setValue = function() { return "true"; };
        simplifyScorm.commit = function() { return "true"; };
        simplifyScorm.terminate = function() { return "true"; };
      } else {
        simplifyScorm.LMSSetValue = function() { return "true"; };
        simplifyScorm.LMSCommit = function() { return "true"; };
        simplifyScorm.LMSFinish = function() { return "true"; };
      }
      
      return;
    }
  
    // The rest of the function only runs if not in view-only mode
    
    // Attempt to restore previous SCORM/xAPI state
    const firstState = xApiFunctions.myGetState();
  
    if (firstState && firstState.suspend_data) {
      const decodedState = decodeSuspendData(firstState.suspend_data);
      if (decodedState.courseComplete) {
        debugLog('Course was previously completed. Triggering completion logic.');
        handleCourseCompletion();
      }
  
      if (isScorm2004) {
        simplifyScorm.cmi.entry = 'resume';
        simplifyScorm.cmi.location = decodedState.location || simplifyScorm.cmi.location;
        simplifyScorm.cmi.mode = decodedState.mode || simplifyScorm.cmi.mode;
        simplifyScorm.cmi.learner_preference.audio_captioning = decodedState.preferences?.audio_captioning || simplifyScorm.cmi.learner_preference.audio_captioning;
        simplifyScorm.cmi.learner_preference.audio_level = decodedState.preferences?.audio_level || simplifyScorm.cmi.learner_preference.audio_level;
        simplifyScorm.cmi.learner_preference.delivery_speed = decodedState.preferences?.delivery_speed || simplifyScorm.cmi.learner_preference.delivery_speed;
        simplifyScorm.cmi.learner_preference.language = decodedState.preferences?.language || simplifyScorm.cmi.learner_preference.language;
      } else {
        simplifyScorm.cmi.core.entry = 'resume';
        simplifyScorm.cmi.core.lesson_location = decodedState.location || simplifyScorm.cmi.core.lesson_location;
        simplifyScorm.cmi.core.lesson_mode = decodedState.mode || simplifyScorm.cmi.core.lesson_mode;
        simplifyScorm.cmi.student_preference.text = decodedState.preferences?.audio_captioning || simplifyScorm.cmi.student_preference.text;
        simplifyScorm.cmi.student_preference.audio = decodedState.preferences?.audio_level || simplifyScorm.cmi.student_preference.audio;
        simplifyScorm.cmi.student_preference.speed = decodedState.preferences?.delivery_speed || simplifyScorm.cmi.student_preference.speed;
        simplifyScorm.cmi.student_preference.language = decodedState.preferences?.language || simplifyScorm.cmi.student_preference.language;
      }
      simplifyScorm.cmi.suspend_data = decodedState.suspend_data || simplifyScorm.cmi.suspend_data;
    }
  
    // On SCORM init
    simplifyScorm.on(APIwrapper.getNameInit(isScorm2004), () => {
      xApiFunctions.initializeAttempt();
      if (isScorm2004) {
        simplifyScorm.cmi.completion_status = 'incomplete';
      } else {
        simplifyScorm.cmi.core.lesson_status = 'incomplete';
      }
    });
  
    // On SCORM commit (periodic save)
    simplifyScorm.on(APIwrapper.getNameCommit(isScorm2004), () => {
      xApiFunctions.setAttemptState();
    });
  
    // Track whether completion has already been triggered to avoid duplicate calls
    let completionTriggered = false;

    // On SCORM set value => check if completed/passed
    simplifyScorm.on(APIwrapper.getNameSet(isScorm2004), (name, value) => {
      clearTimeout(delayXApiRequest);
      delayXApiRequest = setTimeout(() => {
        xApiFunctions.setAttemptState();
      }, 1000);

      xApiFunctions.saveDataValue(name, value);

      if (completionTriggered) return;

      const completionStatuses = ['completed', 'passed'];
      if (isScorm2004) {
        const completionAchieved =
          (name === 'cmi.completion_status' && completionStatuses.includes(value)) ||
          (name === 'cmi.success_status' && completionStatuses.includes(value));

        if (completionAchieved) {
          completionTriggered = true;
          handleCourseCompletion();
          return;
        }

        // Mastery score check: if content sets a scaled score, compare against
        // the scaled passing score (set by the LMS or manifest) to derive pass/fail
        if (name === 'cmi.score.scaled' || name === 'cmi.score.raw') {
          const passingScore = parseFloat(simplifyScorm.cmi?.scaled_passing_score);
          if (!isNaN(passingScore)) {
            const score = parseFloat(value);
            if (!isNaN(score) && score >= passingScore) {
              completionTriggered = true;
              handleCourseCompletion();
            }
          }
        }
      } else {
        // SCORM 1.2
        if (name === 'cmi.core.lesson_status' && completionStatuses.includes(value)) {
          completionTriggered = true;
          handleCourseCompletion();
          return;
        }

        // Mastery score check for SCORM 1.2: compare cmi.core.score.raw against
        // cmi.student_data.mastery_score to derive pass/fail when content only reports a score
        if (name === 'cmi.core.score.raw') {
          const masteryScore = parseFloat(simplifyScorm.cmi?.student_data?.mastery_score);
          if (!isNaN(masteryScore)) {
            const score = parseFloat(value);
            if (!isNaN(score) && score >= masteryScore) {
              completionTriggered = true;
              handleCourseCompletion();
            }
          }
        }
      }
    });
  
    // On SCORM finish
    simplifyScorm.on(APIwrapper.getNameFinish(isScorm2004), () => {
      xApiFunctions.terminateAttempt();
    });
  }

  async function getXapiCompletionStatus(email, activityId, endpoint, username, password, courseId, lessonId, currentRegistrationId) {
    // FIXED: Add early return if lessonId is null
    if (!lessonId || lessonId === 'null') {
      debugLog(`Skipping xAPI status check - lessonId is null for course ${courseId}`);
      return {
        completed: false,
        registrationId: currentRegistrationId,
        skipTracking: true // Flag to indicate this shouldn't be tracked
      };
    }
    
    debugLog(`Checking xAPI status for ${email} on activity ${activityId}, lesson ${lessonId}`);
    
    try {
      // Create agent object for the user
      const agent = {
        objectType: 'Agent',
        mbox: `mailto:${email}`
      };
      
      // Create query parameters for statement search
      const queryParams = new URLSearchParams();
      queryParams.append('agent', JSON.stringify(agent));
      
      // Add activity if valid
      if (activityId && activityId.trim() !== '') {
        queryParams.append('activity', activityId);
        queryParams.append('related_activities', 'true');
      }
      
      // If we have a registration ID, include it - must be valid UUID
      if (currentRegistrationId) {
        queryParams.append('registration', currentRegistrationId);
        debugLog(`Filtering by registration ID: ${currentRegistrationId} for lesson-------------- ${lessonId}`);
      }
      
      // Auth header for LRS
      const authHeader = 'Basic ' + btoa(`${username}:${password}`);
      
      const response = await fetch(`${endpoint}statements?${queryParams}`, {
        method: 'GET',
        headers: {
          'X-Experience-API-Version': '1.0.3',
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`LRS responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // If no statements found but we have a currentRegistrationId, that's fine
      if ((!data.statements || data.statements.length === 0) && currentRegistrationId) {
        return {
          completed: false,
          registrationId: currentRegistrationId
        };
      }
      
      // Check for completion statements
      if (data.statements && data.statements.length > 0) {
        const completedStatements = data.statements.filter(statement => {
          if (!statement.verb) return false;
          // Check verb ID against known completion verbs
          const hasCompletionVerb = XAPI_COMPLETION_VERBS.includes(statement.verb.id);
          // Some authoring tools (Storyline, Rise, Captivate) set result fields
          // instead of or alongside specific verbs
          const hasCompletionResult = statement.result?.completion === true;
          const hasSuccessResult = statement.result?.success === true;
          return hasCompletionVerb || hasCompletionResult || hasSuccessResult;
        });
        
        if (completedStatements.length > 0) {
          // Filter by our registration ID if specified
          const relevantStatements = currentRegistrationId
            ? completedStatements.filter(s => s.context?.registration === currentRegistrationId)
            : completedStatements;
            
          if (relevantStatements.length > 0) {
            const mostRecent = relevantStatements.sort((a, b) => 
              new Date(b.timestamp) - new Date(a.timestamp)
            )[0];
            
            return {
              completed: true,
              timestamp: mostRecent.timestamp,
              registrationId: currentRegistrationId || mostRecent.context?.registration
            };
          }
        }
      }
      
      // Default response if no completion statements found
      return { 
        completed: false,
        registrationId: currentRegistrationId
      };
    } catch (error) {
      debugLog('Error checking xAPI status', error.message);
      return { 
        completed: false, 
        error: error.message,
        registrationId: currentRegistrationId
      };
    }
  }


/**
 * Gets a stored registration ID for a specific lesson
 * Fixed to match the exact storage pattern used in generateUniqueRegistrationId
 */
function getStoredRegistrationId(userId, courseId, lessonId, email) {
    if (!userId || !courseId || !lessonId) {
      debugLog('Error: Missing required IDs for registration retrieval', {userId, courseId, lessonId});
      return null;
    }
    
    // IMPORTANT: This must match the key pattern in generateUniqueRegistrationId EXACTLY
    const uniqueKey = `${REGISTRATION_STORAGE_PREFIX}${userId}|${courseId}|${lessonId}`;
    debugLog(`GET: Looking for registration ID with key: "${uniqueKey}" for lesson ${lessonId}`);
    
    const regId = localStorage.getItem(uniqueKey);
    
    if (regId) {
      debugLog(`GET: Found registration ID for lesson ${lessonId}: ${regId}`);
    } else {
      debugLog(`GET: No registration ID found for lesson ${lessonId} with key "${uniqueKey}"`);
    }
    
    return regId;
}


async function setupRegistrationId(courseData, userData, coursesBaseUrl) {
    if (!courseData || !userData) {
      debugLog('Error: Missing courseData or userData');
      return uuidv4();
    }
    
    try {
      const userId = userData.id;
      const courseId = courseData.courseId;
      const lessonId = courseData.lessonId;
      const email = userData.email;
      
      debugLog('SETUP: Setting up registration ID for:', { userId, courseId, lessonId });
      
      if (!userId || !courseId) {
        debugLog('Error: Missing required IDs for registration setup');
        return uuidv4();
      }
      
      // FIXED: Handle null lessonId case
      if (!lessonId || lessonId === 'null') {
        debugLog('SETUP: No lessonId provided, generating standalone course registration ID');
        // For courses without lessons, use a different key pattern
        const standaloneCourseKey = `${REGISTRATION_STORAGE_PREFIX}${userId}|${courseId}|standalone`;
        const existingStandaloneId = localStorage.getItem(standaloneCourseKey);
        
        if (existingStandaloneId) {
          return existingStandaloneId;
        }
        
        const newStandaloneId = uuidv4();
        localStorage.setItem(standaloneCourseKey, newStandaloneId);
        return newStandaloneId;
      }
      
      // Check for existing registration ID in localStorage first
      const existingId = getStoredRegistrationId(userId, courseId, lessonId, email);
      
      if (existingId) {
        debugLog(`SETUP: Using existing registration ID for lesson ${lessonId}: ${existingId}`);
        return existingId;
      }
      
      // IMPORTANT: No existing ID in localStorage, so instead of checking LRS,
      // we generate a new ID immediately to avoid LRS returning the same ID for different lessons
      const newId = uuidv4();
      debugLog(`SETUP: Creating new random ID for lesson ${lessonId}: ${newId}`);
      
      // Store the new ID
      const uniqueKey = `${REGISTRATION_STORAGE_PREFIX}${userId}|${courseId}|${lessonId}`;
      localStorage.setItem(uniqueKey, newId);
      
      return newId;
    } catch (error) {
      debugLog('Error in setupRegistrationId', error);
      return uuidv4();
    }
  }


/**
 * xAPI Initialization
 */
function initXapi(courseData, userData, registrationId, handleCourseCompletion) {
  resetScorm();
  
  // Flag to track completion to prevent duplicates
  let completionAttempted = false;

  const xApiFunctions = scormToXAPIFunctions.xapi();
  if (!xApiFunctions) {
    debugLog('scormToXAPIFunctions.xapi() returned undefined');
    return;
  }

  window.xapi = xApiFunctions;
  debugLog(`Initializing xAPI with registration ID: ${registrationId}`);

  // Build & store xAPI config with the specific registration ID
  const xApiConfig = getXapiConfig(courseData.courseId, courseData.name, false, registrationId);
  window.localStorage.APIScormConfig = JSON.stringify(xApiConfig);

  // Store learnerId in localStorage
  window.localStorage.learnerId = userData.email;

  // Attempt to restore xAPI state
  const firstState = xApiFunctions.myGetState();

  // Decode suspend_data
  let decodedState = {};
  if (firstState && firstState.suspend_data) {
    decodedState = decodeSuspendData(firstState.suspend_data);
    
    if (decodedState.courseComplete) {
      debugLog('Found courseComplete flag in state. Triggering completion logic.');
      handleCourseCompletion();
      completionAttempted = true;
    }
  }

  // Store the registration ID - FIXED: Handle null lessonId
  let storageKey;
  if (courseData.lessonId && courseData.lessonId !== 'null') {
    storageKey = `${REGISTRATION_STORAGE_PREFIX}${userData.id}_${courseData.courseId}_${courseData.lessonId}`;
  } else {
    storageKey = `${REGISTRATION_STORAGE_PREFIX}${userData.id}_${courseData.courseId}_standalone`;
  }
  localStorage.setItem(storageKey, registrationId);

  // Monitor statement sends for completion
  if (typeof xApiFunctions.sendStatement === 'function') {
    const originalSendStatement = xApiFunctions.sendStatement.bind(xApiFunctions);
    xApiFunctions.sendStatement = (statement) => {
      // Check for completion verbs or result fields
      if (!completionAttempted && statement && statement.verb) {
        const hasCompletionVerb = XAPI_COMPLETION_VERBS.includes(statement.verb.id);
        const hasCompletionResult = statement.result?.completion === true;
        const hasSuccessResult = statement.result?.success === true;

        if (hasCompletionVerb || hasCompletionResult || hasSuccessResult) {
          debugLog('Detected completion statement:', statement);
          completionAttempted = true;
          
          // Mark courseComplete = true in state
          const currentState = decodeSuspendData(xApiFunctions.myGetState()?.suspend_data || '{}');
          if (!currentState.courseComplete) {
            const updatedState = { ...currentState, courseComplete: true };
            try {
              xApiFunctions.setAttemptState(JSON.stringify(updatedState));
            } catch (error) {
              debugLog('Error updating state:', error);
            }
          }
        }
      }
      
      // Continue with original function
      return originalSendStatement(statement);
    };
  }

  // Override terminateAttempt
  if (typeof xApiFunctions.terminateAttempt === 'function') {
    const originalTerminate = xApiFunctions.terminateAttempt.bind(xApiFunctions);
    xApiFunctions.terminateAttempt = () => {
      if (!completionAttempted) {
        const currentState = decodeSuspendData(xApiFunctions.myGetState()?.suspend_data || '{}');
        
        if (currentState.courseComplete) {
          completionAttempted = true;
          handleCourseCompletion();
        }
      }

      // Call original function
      try {
        originalTerminate();
      } catch (error) {
        debugLog('Error in terminateAttempt:', error);
      }
    };
  }
  
  return true;
}

/** 
 * Helper functions to parse IMSManifest or tc-config.js 
 */
function processItems(items) {
  if (!Array.isArray(items)) {
    return getContent(items);
  }
  return items.map((item) => {
    const content = getContent(item);
    if (Array.isArray(item.item)) {
      content.items = processItems(item.item);
    }
    return content;
  });
}

function getContent(item) {
  return {
    title: item.title,
    href: item.href,
    identifierref: item.identifierref,
    identifier: item.identifier,
    parameters: item.parameters
  };
}

function setHref(item, resources) {
  if (item.identifierref) {
    let resource = null;
    if (Array.isArray(resources)) {
      resource = resources.find((r) => r.identifier === item.identifierref);
    } else {
      resource = resources;
    }
    if (resource) {
      item.href = resource.href;
      item.type = resource.type;
      item.file = resource.file;
      item.scormtype = resource['adlcp:scormtype'];
    }
  }
  if (item.items) {
    item.items.forEach((subItem) => setHref(subItem, resources));
  }
}

function extractTcConfig(content, key) {
  const regexMap = {
    TC_COURSE_ID: /TC_COURSE_ID\s*=\s*"([^"]*)"/,
    TC_COURSE_NAME: /TC_COURSE_NAME\s*=\s*({\s*"\s*en-US\s*"\s*:\s*"[^"]*"\s*}\s*);/,
    TC_COURSE_DESC: /TC_COURSE_DESC\s*=\s*({\s*"\s*en-US\s*"\s*:\s*"[^"]*"\s*}\s*);/
  };
  const regex = regexMap[key];
  if (!regex) return null;
  const match = content.match(regex);
  return match && match[1] ? match[1] : null;
}

function encodeUrlParams(params) {
  const encodedParams = [];
  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      const encodedKey = encodeURIComponent(key);
      const encodedValue = encodeURIComponent(params[key]);
      encodedParams.push(encodedKey + '=' + encodedValue);
    }
  }
  return encodedParams.join('&');
}

// Debug helper function
function debugLog(message, data) {
  console.log(`[SCORM Debug] ${message}`, data || '');
}

// ScormCourse component
function ScormCourse({ companyId, scormCourseId }) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Extract params from URL
  const userId = queryParams.get('userId');
  const name = queryParams.get('name');
  const email = queryParams.get('email');
  const scormContentName = queryParams.get('scormContentName');
  const scormType = queryParams.get('scormType');
  const courseId = queryParams.get('courseId');
  const lessonId = queryParams.get('lessonId');
  let isLessonCompleted = queryParams.get('isLessonCompleted');
  const hostname = queryParams.get('hostname');
  const passingScoreParam = queryParams.get('passingScore');
  const authTokenRef = useRef(null);
  const xapiInitializedRef = useRef(false);
  
  debugLog('URL params', { userId, name, email, scormType, courseId, lessonId, isLessonCompleted, hostname });
  
  const [dataLoaded, setDataLoaded] = useState(false);
  const [userData, setUserData] = useState(null);
  const [courseData, setCourseData] = useState(null);
  const [scormInitialized, setScormInitialized] = useState(false);
  const [courseMenuData, setCourseMenuData] = useState(null);
  const [coursePageLink, setCoursePageLink] = useState(null);
  const [hasChapters, setHasChapters] = useState(true);
  const [viewOnlyMode, setViewOnlyMode] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  
  // Track completion attempts to prevent loops
  const completionAttemptedRef = useRef(false);
  // Track polling to prevent multiple polls
  const pollTimeoutRef = useRef(null);
  // Track isLessonCompleted to persist across renders
  const isLessonCompletedRef = useRef(isLessonCompleted);

  const coursesBaseUrl = GetScormCoursesBaseUrl();

  // Update ref when isLessonCompleted changes
  useEffect(() => {
    isLessonCompletedRef.current = isLessonCompleted;
  }, [isLessonCompleted]);

  const markLessonCompleted = useCallback(async (courseId, lessonId, userId, companyId, authToken, hostname) => {
    if (!courseId || !userId) {
      console.error('Missing parameters to mark lesson complete.');
      return;
    }
    
    // FIXED: Handle null lessonId case
    if (!lessonId || lessonId === 'null') {
      debugLog('Skipping lesson completion - no lessonId provided for standalone course');
      return;
    }
  
      baseUrl = BACKEND_API_URL;
      redirectURL = hostname === "localhost" ? "http://localhost:5173" : WEB_URL;
  
      const endpoint = `${baseUrl}/user/course/progress`;
  
    try {
      const lessonStatus = 'completed'; // or 'passed', depending on your logic
     
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({ courseInfoId: courseId,lessonInfoId: lessonId,status: lessonStatus,companyInfoId: companyId})
      });
  
      if (!response.ok) {
        throw new Error(`Error completing lesson: ${response.status} ${response.statusText}`);
      }
  
      const data = await response.json();
      debugLog('Lesson marked as completed successfully:', data);
    } catch (error) {
      console.error('Failed to mark lesson as completed:', error);
    }finally{
      if (window.parent) {
          window.parent.postMessage(
            {
              type: 'lesson-completed',
              courseId,
              lessonId
            },
            `${redirectURL}/courses/digital/${courseId}/lesson/${lessonId}` // Svelte app origin
          );
        }
    }
  }, []); // Empty dependency array since it uses parameters passed to it
  
// Add a ref for the ScormCourseViewer
const scormViewerRef = useRef(null);
  
// Modified handleCourseCompletion function to avoid infinite loops
const handleCourseCompletion = useCallback(() => {
  // FIXED: Only attempt completion if we have a valid lessonId and not already completed
  if ((!lessonId || lessonId === 'null') && !completionAttemptedRef.current) {
    debugLog('Skipping completion handling - no valid lessonId for standalone course');
    return;
  }

  // Only attempt completion if not already completed and not already attempted
  if (isLessonCompletedRef.current === 'false' && !completionAttemptedRef.current) {
    // Set the flag immediately to prevent repeated calls
    completionAttemptedRef.current = true;
    
    // Mark the lesson as completed
    markLessonCompleted(courseId, lessonId, userId, companyId, authTokenRef.current, hostname)
      .then(() => {
        // Update local state
        setViewOnlyMode(true);
        
        // Update the ref to track completion status
        isLessonCompletedRef.current = 'true';
        
        // If we're using xAPI, also reset the ScormCourseViewer
        if (courseData?.scormType === 'xapi' && scormViewerRef.current) {
          scormViewerRef.current.resetSource();
        }
      })
      .catch((error) => {
        console.error('Error in course completion:', error);
        // Reset the flag so we can try again
        completionAttemptedRef.current = false;
      });
  }
}, [courseId, lessonId, userId, companyId, hostname, courseData, markLessonCompleted]);


    /**
 * Clear all invalid registration IDs
 * Run this once to fix existing issues
 */
function clearInvalidRegistrationIds() {
    const allKeys = Object.keys(localStorage);
    
    // Find all registration keys
    const regKeys = allKeys.filter(key => key.startsWith(REGISTRATION_STORAGE_PREFIX));
    let removed = 0;
    
    regKeys.forEach(key => {
      const value = localStorage.getItem(key);
      
      // Check if the stored ID has a prefix (invalid for LRS)
      if (value && (value.startsWith('L') || value.includes('_'))) {
        debugLog(`Removing invalid registration ID: ${value}`);
        localStorage.removeItem(key);
        removed++;
      }
    });
    
    debugLog(`Cleared ${removed} invalid registration IDs`);
  }
  
  /**
   * Add this to your component initialization once
   */
  useEffect(() => {
    // Run this once to fix any existing invalid registration IDs
    clearInvalidRegistrationIds();
  }, []);

  // Auth token listener
  useEffect(() => {
    const handleMessage = (event) => {
      if (typeof event.data === 'object' && event.data?.token) {
        authTokenRef.current = event.data.token;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Initialize user & course data
  useEffect(() => {
    setUserData({ id: userId, name, email });
    setCourseData({
      id: scormCourseId,
      companyId,
      name: scormContentName,
      scormType,
      courseId,
      lessonId
    });
    
    // Set view-only mode if lesson is already completed
    if (isLessonCompleted === 'true') {
      setViewOnlyMode(true);
      // Also mark completion as attempted to avoid API calls
      completionAttemptedRef.current = true;
    }
  }, [
    companyId, scormCourseId, userId, name, email, 
    scormContentName, scormType, courseId, lessonId, isLessonCompleted
  ]);

   /**
   * Modified function for initial xAPI setup in the component
   */
   useEffect(() => {
    async function initRegistrationId() {
      // IMPORTANT CHANGE: Always generate a new ID for each lesson, never use LRS lookups for IDs
      // This ensures each lesson gets a unique registration ID
      const regId = await setupRegistrationId(courseData, userData, coursesBaseUrl);
      setRegistrationId(regId);
      
      // FIXED: Handle null lessonId in debug logging
      if (courseData.lessonId && courseData.lessonId !== 'null') {
        if (!regId.includes(`L${courseData.lessonId}`)) {
          debugLog(`Registration ID ${regId} doesn't contain lesson ID prefix. This may cause issues.`);
        } else {
          debugLog(`Using lesson-specific registration ID: ${regId}`);
        }
      } else {
        debugLog(`Using standalone course registration ID: ${regId}`);
      }
    }
    
    if (courseData && userData) {
      initRegistrationId();
    }
  }, [courseData, userData, coursesBaseUrl]);


  // MAIN INITIALIZATION EFFECT
  useEffect(() => {
    if (!courseData || !userData || !registrationId || scormInitialized) {
      return;
    }
    
    // Set the path regardless of content type
    courseData.path = `${courseData.companyId}/${courseData.id}`;
    const lang = 'en-US';

    // ------- SCORM 1.2 or 2004 -------
    if (courseData.scormType === 'scorm12' || courseData.scormType === 'scorm2004') {
        const isScorm2004 = courseData.scormType === 'scorm2004';
        const manifestPath = `${coursesBaseUrl}/${courseData.path}/imsmanifest.xml`;
      
        fetch(manifestPath)
          .then((response) => response.text())
          .then((xmlData) => {
            parseString(xmlData, { explicitArray: false, mergeAttrs: true }, (err, result) => {
              if (!err) {
                const courseMenu = {};
                courseMenu.title = result.manifest.organizations.organization.title;
                courseMenu.href = null;
                courseMenu.content = processItems(result.manifest.organizations.organization.item);
      
                if (Array.isArray(courseMenu.content)) {
                  courseMenu.content.forEach((item) =>
                    setHref(item, result.manifest.resources.resource)
                  );
                } else {
                  setHref(courseMenu.content, result.manifest.resources.resource);
                  courseMenu.content = [courseMenu.content];
                }
                setCourseMenuData(courseMenu);
      
                // If there's only one content item, open it automatically
                if (courseMenu.content && courseMenu.content.length === 1) {
                  setCoursePageLink(
                    courseMenu.content[0].href + (courseMenu.content[0].parameters || '')
                  );
                  setHasChapters(false);
                }
      
                setDataLoaded(true);

                // Extract passing score from manifest (SCORM 2004: minNormalizedMeasure, completionThreshold)
                let manifestPassingScore = null;
                try {
                  const org = result.manifest.organizations?.organization;
                  // Check for imsss:minNormalizedMeasure in sequencing rules
                  const sequencing = org?.['imsss:sequencing'] || org?.sequencing;
                  const objectives = sequencing?.['imsss:objectives'] || sequencing?.objectives;
                  const primaryObj = objectives?.['imsss:primaryObjective'] || objectives?.primaryObjective;
                  if (primaryObj?.['imsss:minNormalizedMeasure'] !== undefined) {
                    manifestPassingScore = primaryObj['imsss:minNormalizedMeasure'];
                  } else if (primaryObj?.minNormalizedMeasure !== undefined) {
                    manifestPassingScore = primaryObj.minNormalizedMeasure;
                  }
                  // Also check completionThreshold at manifest level
                  if (manifestPassingScore === null) {
                    const threshold = result.manifest['adlcp:completionThreshold'] || result.manifest.completionThreshold;
                    if (threshold !== undefined) {
                      manifestPassingScore = typeof threshold === 'object' ? threshold.minProgressMeasure : threshold;
                    }
                  }
                  if (manifestPassingScore !== null) {
                    debugLog(`Found passing score in manifest: ${manifestPassingScore}`);
                  }
                } catch (e) {
                  debugLog('Could not extract passing score from manifest:', e);
                }

                // Priority: URL param > manifest value
                const effectivePassingScore = passingScoreParam || manifestPassingScore;
                if (effectivePassingScore) {
                  debugLog(`Using passing score: ${effectivePassingScore} (source: ${passingScoreParam ? 'URL param' : 'manifest'})`);
                }

                // Check if this should be view-only mode
                const isViewOnly = isLessonCompleted === 'true' || viewOnlyMode;

                // Pass the view-only flag and passing score to initScorm
                initScorm(
                  courseData,
                  userData,
                  isScorm2004,
                  registrationId,
                  handleCourseCompletion,
                  isViewOnly,
                  effectivePassingScore
                );
                
                setScormInitialized(true);
                
                // Update the UI if in view-only mode
                if (isViewOnly) {
                  setViewOnlyMode(true);
                }
              } else {
                console.error(`Error parsing ${manifestPath}:`, err);
              }
            });
          })
          .catch((error) => {
            console.error(`Failed to fetch or parse ${manifestPath}:`, error);
          });
      }
    // ------- xAPI -------
  

    else if (courseData.scormType === 'xapi' && !xapiInitializedRef.current) {
        // Set the initialization flag to prevent future runs
        xapiInitializedRef.current = true;
        const tcConfigPath = `${coursesBaseUrl}/${courseData.path}/tc-config.js`;
        
        fetch(tcConfigPath)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to fetch tc-config: ${response.status}`);
            }
            return response.text();
          })
          .then(async (fileContent) => {
            try {
              const activityId = extractTcConfig(fileContent, 'TC_COURSE_ID');
              const courseNameObj = extractTcConfig(fileContent, 'TC_COURSE_NAME');
              
              let courseName = 'Course';
              if (courseNameObj) {
                try {
                  const parsedName = JSON.parse(courseNameObj);
                  if (parsedName && parsedName[lang]) {
                    courseName = parsedName[lang];
                  }
                } catch (e) {
                  // Ignore parsing errors
                }
              }
      
              // Check xAPI status ONLY if lessonId exists and is valid
              
              let completionStatus = null;
              
              // FIXED: Only check completion status if lessonId is not null
              if (lessonId && lessonId !== 'null') {
                try {
                  completionStatus = await getXapiCompletionStatus(
                    userData.email,
                    activityId,
                    LRS_ENDPOINT,
                    LRS_API_KEY,
                    LRS_API_KEY_SECRETE,
                    courseId,
                    lessonId,
                    registrationId
                  );
                  
                  // Store completion status in a variable to avoid state update issues
                  const isCompleted = completionStatus?.completed;
                  console.log('[xAPI] Completion status:', isCompleted);
                  
                  // If completed in LRS but not in our system and not already attempting completion
                  if (isCompleted && isLessonCompleted === 'false' && !completionAttemptedRef.current) {
                    // Set the flag to prevent repeated attempts
                    completionAttemptedRef.current = true;
                    handleCourseCompletion();
                    // Don't set view mode here - let handleCourseCompletion do it
                  } 
                  // If completed in our system
                  else if (isLessonCompleted === 'true') {
                    setViewOnlyMode(true);
                  }
                } catch (error) {
                  console.error("Error checking xAPI completion status:", error);
                }
              } else {
                debugLog("Skipping xAPI completion check - lessonId is null or invalid (standalone course)");
                // For courses without lessons, don't set up completion tracking
              }
      
              // Build the xAPI launch URL with our registration ID
              const actor = { name: [userData.name], mbox: ['mailto:' + userData.email] };
              
              // Determine if we should be in view-only mode based on local variables
              // NOT from state which might not be updated yet
              const shouldBeViewOnly = isLessonCompleted === 'true' || 
                                     (completionStatus && completionStatus.completed);
              
              let params;
              if (!shouldBeViewOnly) {
                // Full tracking parameters
                const lrsAuth = 'Basic ' + btoa(LRS_API_KEY + ':' + LRS_API_KEY_SECRETE);
                params = {
                  actor: JSON.stringify(actor),
                  endpoint: LRS_ENDPOINT,
                  auth: lrsAuth,
                  activity_id: activityId,
                  registration: registrationId,
                  grouping: activityId
                };
              } else {
                // View-only mode parameters
                params = {
                  actor: JSON.stringify(actor),
                  activity_id: activityId,
                  registration: registrationId,
                  view_only: 'true'
                };
                setViewOnlyMode(true);
              }
              
              const encodedParams = encodeUrlParams(params);
              const pageLink = `index.html?${encodedParams}`;
      
              // Set these all at once to minimize re-renders
              const courseMenu = {
                title: courseName,
                content: [{ title: courseName, href: pageLink }]
              };
              
              // Group state updates in a single render cycle if possible
              setCourseMenuData(courseMenu);
              setCoursePageLink(pageLink);
              setHasChapters(false);
              setDataLoaded(true);
              setScormInitialized(true);
              
              // FIXED: Only initialize xAPI tracking if we have a valid lessonId
              if (!shouldBeViewOnly && lessonId && lessonId !== 'null') {
                initXapi(courseData, userData, registrationId, handleCourseCompletion);
              }
            } catch (error) {
              console.error("Error in xAPI initialization:", error);
              setDataLoaded(true);
            }
          })
          .catch((error) => {
            console.error("Error fetching tc-config:", error);
            setDataLoaded(true);
          });
      }
    else {
      // Handle unknown content type
      setDataLoaded(true);
    }
    
  }, [
    companyId,
    scormCourseId,
    coursesBaseUrl,
    courseData,
    userData,
    scormInitialized,
    isLessonCompleted,
    viewOnlyMode,
    registrationId,
    courseId,
    lessonId,
    handleCourseCompletion
  ]);

  // FIXED: SIMPLIFIED POLLING EFFECT - to prevent infinite loops and handle null lessonId
  useEffect(() => {
    // CRITICAL FIX: Skip polling if lessonId is null or invalid
    if (!dataLoaded || !registrationId || courseData?.scormType !== 'xapi' || 
        viewOnlyMode || isLessonCompleted === 'true' || completionAttemptedRef.current ||
        !lessonId || lessonId === 'null') {
      debugLog('Skipping polling - conditions not met or lessonId invalid', {
        dataLoaded, registrationId, scormType: courseData?.scormType, 
        viewOnlyMode, isLessonCompleted, completionAttempted: completionAttemptedRef.current,
        lessonId
      });
      return;
    }
    
    // Clean up any existing poll
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
    }
    
    const pollOnce = async () => {
      try {
        // Skip if completion has been attempted or lessonId is invalid
        if (completionAttemptedRef.current || !lessonId || lessonId === 'null') {
          debugLog('Polling stopped - completion attempted or invalid lessonId');
          return;
        }
        
        const tcConfigPath = `${coursesBaseUrl}/${courseData.path}/tc-config.js`;
        const response = await fetch(tcConfigPath);
        const fileContent = await response.text();
        const activityId = extractTcConfig(fileContent, 'TC_COURSE_ID');
        
        if (!activityId) return;
        
        const status = await getXapiCompletionStatus(
          userData.email,
          activityId,
          LRS_ENDPOINT,
          LRS_API_KEY,
          LRS_API_KEY_SECRETE,
          courseId,
          lessonId,
          registrationId
        );
        
        // Update UI state only if completed
        if (status?.completed) {
          console.log('[xAPI Polling] Course completed:', status);
          
          // Handle completion if needed
          if (isLessonCompleted === 'false' && !completionAttemptedRef.current) {
            handleCourseCompletion();
          }
        }
        
        // Only schedule another poll if not completed and not attempted and lessonId is valid
        if (!status?.completed && !completionAttemptedRef.current && lessonId && lessonId !== 'null') {
          pollTimeoutRef.current = setTimeout(pollOnce, 30000); // Poll every 30 seconds
        }
      } catch (error) {
        debugLog('Polling error:', error);
        // If error, schedule another poll but less frequently, and only if lessonId is valid
        if (!completionAttemptedRef.current && lessonId && lessonId !== 'null') {
          pollTimeoutRef.current = setTimeout(pollOnce, 60000); // Longer interval on error
        }
      }
    };
    
    // Initial poll delay - only if lessonId is valid
    if (lessonId && lessonId !== 'null') {
      pollTimeoutRef.current = setTimeout(pollOnce, 5000); // 5 second initial delay
    }
    
    // Cleanup
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [
    dataLoaded, 
    courseData, 
    userData, 
    isLessonCompleted,
    viewOnlyMode,
    coursesBaseUrl,
    registrationId,
    courseId,
    lessonId, 
    handleCourseCompletion
  ]);

  // RENDER UI
  if (!dataLoaded) {
    return <Overlay LoadingText="Loading course content..." />;
  }

  return (
    <div className="bg-gray-100" style={{ height: '100vh', overflow: 'hidden' }}>  
      <div className="flex flex-col" style={{ height: '100%' }}>
        {/* Main content */}
        <div className="flex" style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            <CourseMenu
            courseData={courseMenuData}
            hasChapters={hasChapters}
            onPageLinkClick={(link) => {
                // Reset the viewer if we're changing pages
                if (scormViewerRef.current) {
                scormViewerRef.current.resetSource();
                }
                setCoursePageLink(link);
            }}
            />
            <ScormCourseViewer
                ref={scormViewerRef}
                courseRootPath={courseData.path}
                hasChapters={hasChapters}
                coursePageLink={coursePageLink}
            />

      
        </div>

      </div>
    </div>
  );
}

export default ScormCourse;